const STATES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', PR: 'Puerto Rico',
};
const normalize = (value) => String(value ?? '').normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
export const dealerPartnerNumber = (value) => {
  const code = String(value ?? '').trim();
  return /^450\d{4}$/.test(code) ? code : /^\d{3,4}$/.test(code) ? `450${code.padStart(4, '0')}` : '';
};
export const dealerLocation = (dealer) => [
  [dealer.city || dealer.address?.city, dealer.state || dealer.address?.state].filter(Boolean).join(', '),
  dealer.postal || dealer.postalCode || dealer.address?.postalCode,
].filter(Boolean).join(' ');
export const formatDealerLabel = (dealer) => [dealer.name || dealer.code, dealerLocation(dealer)]
  .filter(Boolean).join(' — ') + (dealer.active === false ? ' (No longer available)' : '');

export const matchesDealerSearch = (dealer, input) => {
  const state = dealer.state || dealer.address?.state || '';
  const searchable = normalize([dealer.name, dealer.city, state, STATES[state.toUpperCase()],
    dealer.postal, dealer.postalCode, dealer.address?.city, dealer.address?.postalCode,
    dealer.code, dealer.porschePartnerNo, dealerPartnerNumber(dealer.code), dealer.label,
  ].filter(Boolean).join(' '));
  return normalize(input).split(' ').filter(Boolean).every((token) => searchable.includes(token));
};
export const filterDealerOption = (candidate, input) =>
  matchesDealerSearch(candidate.data?.dealer || candidate.data, input);
export const toDealerSelection = (dealer) => Object.fromEntries([
  'id', 'code', 'name', 'city', 'state', 'postal', 'porschePartnerNo', 'active',
].filter((key) => dealer[key] !== undefined).map((key) => [key, dealer[key]]));

export const buildDealerOptions = (scope, catalog = [], { includeNoPreference = true, currentValues = [] } = {}) => {
  const records = scope?.length ? scope.map((dealer) => {
    const partner = dealerPartnerNumber(dealer.porschePartnerNo || dealer.code);
    const fresh = catalog.find((item) => partner && dealerPartnerNumber(item.porschePartnerNo || item.code) === partner);
    return fresh ? { ...dealer, ...fresh, code: dealer.code } : dealer;
  }) : catalog.filter((dealer) => dealer.active !== false || currentValues.some((code) =>
    code && dealerPartnerNumber(code) === dealerPartnerNumber(dealer.code))).map((dealer) => {
    const current = currentValues.find((code) => code && code !== '--' &&
      dealerPartnerNumber(code) && dealerPartnerNumber(code) === dealerPartnerNumber(dealer.code));
    return current ? { ...dealer, code: current } : dealer;
  });
  const options = records.map((dealer) => ({
    value: dealer.code, label: formatDealerLabel(dealer), dealer, isDisabled: dealer.active === false,
  })).sort((a, b) => a.label.localeCompare(b.label));
  // Keep historical selections visible even after a dealer has left the live directory.
  for (const code of currentValues) {
    if (code && code !== '--' && !options.some((option) => option.value === code)) {
      const fresh = catalog.find((dealer) => dealerPartnerNumber(dealer.code) === dealerPartnerNumber(code));
      const dealer = fresh ? { ...fresh, code } : { code, name: code, active: false };
      options.push({ value: code, label: formatDealerLabel(dealer), dealer, isDisabled: dealer.active === false });
    }
  }
  return includeNoPreference ? [{ value: '--', label: 'No preference' }, ...options] : options;
};

export const loadDealerOptions = async (scope, loadCatalog, options) => {
  let catalog;
  try {
    catalog = await loadCatalog();
    if (!Array.isArray(catalog)) throw new Error('The dealer directory could not be loaded.');
  } catch (error) {
    // An existing approved event list must remain usable if directory refresh is unavailable.
    if (!scope?.length) throw error;
    catalog = scope;
  }
  return buildDealerOptions(scope, catalog, options);
};
